import React, { useState, useRef, useEffect, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { FaPlay, FaPause } from 'react-icons/fa';
import SubtitleLayer from './SubtitleLayer';
import { useKaraokeSync } from '../hooks/useKaraokeSync';
import './VoicePlayer.css';

const VoicePlayer = ({
  voiceUrl,
  duration,
  compact = false,
  waveColor,
  progressColor,
  dark = false,
  subtitles // { segments: [...] }
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentTimeContinuous, setCurrentTimeContinuous] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Native audio ref — replaced by audioMock backed by WaveSurfer time
  // so useKaraokeSync gets currentTime without duplicate playback.
  const audioMockRef = useRef(null);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  // Karaoke sync hook
  const { setCallbacks: setSyncCallbacks, seekTo } = useKaraokeSync({
    audio: audioMockRef.current,
    segments: subtitles?.segments,
    isPlaying,
  });

  // Time update callback (for subtitle sync — floored for display)
  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(Math.floor(time));
  }, []);

  // Continuous time callback (for subtitle cursor — exact float)
  const handleTimeUpdateContinuous = useCallback((time) => {
    setCurrentTimeContinuous(time);
  }, []);

  // Segment change callback
  const handleSegmentChange = useCallback((activeIdx, crossfadeIdx) => {
    // Can be used for analytics or UI updates
  }, []);

  // Register callbacks
  useEffect(() => {
    setSyncCallbacks(handleTimeUpdate, handleSegmentChange, handleTimeUpdateContinuous);
  }, [setSyncCallbacks, handleTimeUpdate, handleSegmentChange, handleTimeUpdateContinuous]);

  // Handle seek (subtitle sync)
  const handleSeek = useCallback(() => {
    if (wavesurferRef.current) {
      const t = wavesurferRef.current.getCurrentTime();
      seekTo(t);
    }
  }, [seekTo]);

  useEffect(() => {
    if (!waveformRef.current || !voiceUrl) return;

    // Clear any leftover WaveSurfer from previous render (React strict mode)
    waveformRef.current.innerHTML = '';

    // Create audio mock: a plain object whose currentTime is backed by WaveSurfer.
    // This satisfies useKaraokeSync which only reads audio.currentTime,
    // eliminating the need for a second HTMLAudioElement and duplicate playback.
    const audioMock = { currentTime: 0 };
    audioMockRef.current = audioMock;

    // Initialize WaveSurfer
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: waveColor || '#c8d6e5',
      progressColor: progressColor || '#1877f2',
      cursorColor: 'transparent',
      barWidth: compact ? 2 : 3,
      barGap: compact ? 1 : 2,
      barRadius: 3,
      height: compact ? 32 : 48,
      normalize: true,
      interact: true,
    });

    wavesurferRef.current = wavesurfer;

    // Load audio - URLs like /social-network/uploads/... are proxied through setupProxy
    wavesurfer.load(voiceUrl);

    // Event listeners
    wavesurfer.on('ready', () => {
      setIsLoading(false);
      setTotalDuration(Math.round(wavesurfer.getDuration()));
    });

    wavesurfer.on('play', () => {
      setIsPlaying(true);
    });

    wavesurfer.on('pause', () => {
      setIsPlaying(false);
    });

    wavesurfer.on('finish', () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });

    wavesurfer.on('audioprocess', () => {
      const t = wavesurfer.getCurrentTime();
      setCurrentTime(Math.floor(t));
      audioMock.currentTime = t;
    });

    wavesurfer.on('seek', () => {
      const t = wavesurfer.getCurrentTime();
      audioMock.currentTime = t;
      seekTo(t);
    });

    wavesurfer.on('error', (err) => {
      // Ignore AbortError during cleanup
      if (err?.name === 'AbortError' || (typeof err === 'string' && err.includes('aborted'))) {
        return;
      }
      console.error('WaveSurfer error:', err);
      setError('Failed to load audio');
      setIsLoading(false);
    });

    return () => {
      const ws = wavesurferRef.current;
      wavesurferRef.current = null;
      audioMockRef.current = null;

      if (ws) {
        try { ws.pause(); } catch (e) { /* ignore */ }
        ws.unAll();
      }
    };
  }, [voiceUrl, compact, waveColor, progressColor, seekTo]);

  const togglePlay = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get subtitle metadata (with defaults)
  const subtitleMeta = subtitles?.segments?.[0]?.metadata || {};
  const displayOffsetMs = subtitleMeta.display_offset_ms || 270;
  const crossfadeWindowMs = subtitleMeta.crossfade_window_ms || 200;

  if (error) {
    return (
      <div className={`voice-player ${compact ? 'voice-player-compact' : ''}`}>
        <div className="voice-player-error">{error}</div>
      </div>
    );
  }

  return (
    <div className={`voice-player ${compact ? 'voice-player-compact' : ''}`}>
      <button
        type="button"
        className={`voice-player-btn ${dark ? 'voice-player-btn-dark' : 'voice-player-btn-light'}`}
        onClick={togglePlay}
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="loading-spinner"></div>
        ) : isPlaying ? (
          <FaPause size={34} />
        ) : (
          <FaPlay size={34} />
        )}
      </button>

      <div className="voice-player-content">
        {subtitles?.segments && subtitles.segments.length > 0 && (
          <SubtitleLayer
            segments={subtitles.segments}
            currentTime={currentTimeContinuous}
            isPlaying={isPlaying}
            displayOffsetMs={displayOffsetMs}
            crossfadeWindowMs={crossfadeWindowMs}
          />
        )}
        <div className="waveform-player-container" ref={waveformRef}></div>
        <div className="voice-player-time">
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </div>
      </div>
    </div>
  );
};

export default VoicePlayer;
